import { Brands } from '@framework/brands';
import { getEnvironmentConfig, getEnvVariableDefault } from '../configHelper.js';
const DEFAULT_REDIS_PORT = 6379;
/**
 * Parses a Redis endpoint that may be supplied as either "host" or "host:port".
 *
 * The OPM ElastiCache SSM parameter (`/ctf-opm/elasticache/redis/hostname`) stores the
 * endpoint as `host:port` because nginx upstreams need that format. When the same value
 * is injected into Node.js services via `REDIS_HOSTNAME`, the trailing `:<port>` must be
 * stripped before passing it to `redis.createClient({ socket: { host } })`; otherwise
 * `getaddrinfo` will attempt to resolve `host:port` as a DNS name and fail with
 * `ENOTFOUND`.
 */
export const parseRedisHostname = (rawHostname, fallbackPort) => {
    const match = rawHostname.match(/^(.+):(\d+)$/);
    if (match) {
        return { hostname: match[1], port: parseInt(match[2], 10) };
    }
    return { hostname: rawHostname, port: fallbackPort };
};
const isCacheOn = () => getEnvVariableDefault('CACHE_ON', 'false') === 'true';
const definition = {
    base: {
        sqsUrl: 'missing configuration for cachePurge SQS URL for this environment',
        isConsumerEnabled: true,
        redis: (() => {
            const fallbackPort = parseInt(getEnvVariableDefault('REDIS_PORT', String(DEFAULT_REDIS_PORT)), 10);
            const { hostname, port } = parseRedisHostname(getEnvVariableDefault('REDIS_HOSTNAME', 'localhost'), fallbackPort);
            return {
                hostname,
                port,
                enabled: isCacheOn(),
            };
        })(),
        akamai: {
            [Brands.FOCUS]: {
                enabled: true,
                clientToken: getEnvVariableDefault('PURGE_AKAMAI_FOCUS_CLIENT_TOKEN', 'missing-purge-akamai-focus-client-token'),
                clientSecret: getEnvVariableDefault('PURGE_AKAMAI_FOCUS_CLIENT_SECRET', 'missing-purge-akamai-focus-client-secret'),
                accessToken: getEnvVariableDefault('PURGE_AKAMAI_FOCUS_ACCESS_TOKEN', 'missing-purge-akamai-focus-access-token'),
                host: 'https://akab-uenvfihpqxmrgyxe-42hgfnxwxw3vx274.luna.akamaiapis.net',
                network: 'production',
                action: 'invalidate',
            },
            [Brands.BUNTE]: {
                enabled: true,
                clientToken: getEnvVariableDefault('PURGE_AKAMAI_BUNTE_CLIENT_TOKEN', 'missing-purge-akamai-bunte-client-token'),
                clientSecret: getEnvVariableDefault('PURGE_AKAMAI_BUNTE_CLIENT_SECRET', 'missing-purge-akamai-bunte-client-secret'),
                accessToken: getEnvVariableDefault('PURGE_AKAMAI_BUNTE_ACCESS_TOKEN', 'missing-purge-akamai-bunte-access-token'),
                host: 'https://akab-njkqu4nbhiqe64ov-jz7wocigbpmyitff.luna.akamaiapis.net',
                network: 'production',
                action: 'invalidate',
            },
            [Brands.CHIP]: {
                enabled: true,
                clientToken: getEnvVariableDefault('PURGE_AKAMAI_CHIP_CLIENT_TOKEN', 'missing-purge-akamai-chip-client-token'),
                clientSecret: getEnvVariableDefault('PURGE_AKAMAI_CHIP_CLIENT_SECRET', 'missing-purge-akamai-chip-client-secret'),
                accessToken: getEnvVariableDefault('PURGE_AKAMAI_CHIP_ACCESS_TOKEN', 'missing-purge-akamai-chip-access-token'),
                host: 'https://akab-5ewn6qmmpme2na3q-g5etwaejaennkqab.luna.akamaiapis.net',
                network: 'production',
                action: 'invalidate',
            },
        },
    },
    local: {
        sqsUrl: 'http://localhost:4566/000000000000/cachePurgeEventsQueue',
        akamai: {
            [Brands.FOCUS]: {
                enabled: false,
            },
            [Brands.BUNTE]: {
                enabled: false,
            },
            [Brands.CHIP]: {
                enabled: false,
            },
        },
    },
    local_k8s: {
        sqsUrl: 'http://localstack:4566/000000000000/cachePurgeEventsQueue',
        akamai: {
            [Brands.FOCUS]: {
                enabled: false,
            },
            [Brands.BUNTE]: {
                enabled: false,
            },
            [Brands.CHIP]: {
                enabled: false,
            },
        },
    },
    local_e2e: {
        sqsUrl: 'http://localstack:4566/000000000000/cachePurgeEventsQueue',
        akamai: {
            [Brands.FOCUS]: {
                enabled: false,
            },
            [Brands.BUNTE]: {
                enabled: false,
            },
            [Brands.CHIP]: {
                enabled: false,
            },
        },
    },
    test: {
        akamai: {},
    },
    sandbox: {
        sqsUrl: `https://sqs.eu-central-1.amazonaws.com/757726966663/${getEnvVariableDefault('CACHE_PURGE_EVENTS_QUEUE_NAME', 'cache_purge_events_queue')}`,
        akamai: {
            [Brands.FOCUS]: {
                enabled: false,
            },
            [Brands.BUNTE]: {
                enabled: false,
            },
            [Brands.CHIP]: {
                enabled: false,
            },
        },
    },
    development: {
        sqsUrl: `https://sqs.eu-central-1.amazonaws.com/727602533799/${getEnvVariableDefault('CACHE_PURGE_EVENTS_QUEUE_NAME', 'cache_purge_events_queue')}`,
    },
    staging: {
        sqsUrl: `https://sqs.eu-central-1.amazonaws.com/833612680673/${getEnvVariableDefault('CACHE_PURGE_EVENTS_QUEUE_NAME', 'cache_purge_events_queue')}`,
    },
    production: {
        sqsUrl: `https://sqs.eu-central-1.amazonaws.com/885151169647/${getEnvVariableDefault('CACHE_PURGE_EVENTS_QUEUE_NAME', 'cache_purge_events_queue')}`,
    },
};
export const cachePurgeConfig = () => getEnvironmentConfig(definition);
//# sourceMappingURL=cachePurge.config.js.map